import { fetchToursPopularDestinations } from 'actions/GlobalSearch/GlobalSearchToursActions'
import AssistChip from 'LuxKit/Chips/AssistChip'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import qs from 'qs'
import { useCallback, useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { getToursPopularDestinations, getIsFetchingToursPopularDestinations } from 'selectors/destinationSelectors'
import GlobalSearchQuickFilterButtonsContainer from '../GlobalSearch/QuickFilters/GlobalSearchQuickFilterButtonsContainer'
import GlobalSearchQuickFilterLoadingButtons from '../GlobalSearch/QuickFilters/GlobalSearchQuickFilterLoadingButtons'
import GlobalSearchQuickFiltersContainer from '../GlobalSearch/QuickFilters/GlobalSearchQuickFiltersContainer'
import GlobalSearchQuickFiltersTitle from '../GlobalSearch/QuickFilters/GlobalSearchQuickFiltersTitle'
import { trackEvent } from 'analytics/analytics'
import { searchEventWithContext } from 'analytics/snowplow/events'

import uuidV4 from 'lib/string/uuidV4Utils'

interface Props {
  titleLabel?: string
  titleWeight: 'bold' | 'normal'
  titleColour: 'neutral-eight' | 'neutral-two' | 'light-foreground-alt'
  onDestinationClick?: () => void
}

function TourV2TrendingDestinationsQuickFilters(props: Props) {
  const {
    titleLabel,
    titleColour,
    titleWeight,
    onDestinationClick,
  } = props

  const intl = useIntl()
  const defaultTitleLabel = intl.formatMessage({
    id: 'R5FW5y-Fn1Ros7OrJYcQ',
    defaultMessage: 'Trending destinations',
    description: 'Default title for trending tour destinations quick filters section',
  })
  const resolvedTitleLabel = titleLabel ?? defaultTitleLabel

  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(fetchToursPopularDestinations())
  }, [dispatch])

  const destinations = useAppSelector(getToursPopularDestinations)
  const isLoading = useAppSelector(getIsFetchingToursPopularDestinations)

  const destinationLinks = useMemo<Record<string, string>>(() => destinations.reduce((links, destination) => {
    const query = qs.stringify({
      destinationId: destination.value,
      destinationName: destination.format.mainText,
    })
    return {
      ...links,
      [destination.value]: `/search/tours?${query}`,
    }
  }, {}), [destinations])

  const handleDestinationClick = useCallback((event: any) => {
    const location = event.target.textContent

    trackEvent(searchEventWithContext({
      searchEvent: { type: 'search_bar' },
      sortContext: { value: 'recommended' },
      searchContext: {
        destinationIds: [],
        destinationType: 'location',
        destinations: [location],
        vertical: 'tours',
        searchId: uuidV4(),
      },
    }))
    onDestinationClick?.()
  }, [onDestinationClick])

  if (!isLoading && destinations.length === 0) {
    return null
  }

  return <GlobalSearchQuickFiltersContainer>
    <GlobalSearchQuickFiltersTitle colour={titleColour} weight={titleWeight}>
      {resolvedTitleLabel}
    </GlobalSearchQuickFiltersTitle>
    <GlobalSearchQuickFilterButtonsContainer>
      {isLoading && <GlobalSearchQuickFilterLoadingButtons />}
      {!isLoading && destinations.map(destination =>
        <AssistChip
          size="medium"
          variant="filled"
          key={destination.value}
          to={destinationLinks[destination.value]}
          onClick={handleDestinationClick}
        >
          {destination.format.mainText}
        </AssistChip>)}
    </GlobalSearchQuickFilterButtonsContainer>
  </GlobalSearchQuickFiltersContainer>
}

export default TourV2TrendingDestinationsQuickFilters
